import { useEffect, useRef } from "react";

export const ConfirmationDialog = ({
	title,
	description,
	confirmLabel = "Confirm",
	onConfirm,
	onCancel,
}) => {
	const dialogRef = useRef(null);

	useEffect(() => {
		const previouslyFocused = document.activeElement;
		dialogRef.current?.querySelector("button")?.focus();
		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				event.preventDefault();
				onCancel();
				return;
			}
			if (event.key !== "Tab" || !dialogRef.current) return;
			const focusable = dialogRef.current.querySelectorAll(
				'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
			);
			if (focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			if (previouslyFocused?.isConnected) {
				previouslyFocused.focus();
			}
		};
	}, [onCancel]);

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-xs"
			role="presentation"
			onMouseDown={(event) => {
				if (event.target === event.currentTarget) onCancel();
			}}
		>
			<div
				ref={dialogRef}
				role="alertdialog"
				aria-modal="true"
				aria-labelledby="confirmation-dialog-title"
				aria-describedby="confirmation-dialog-description"
				className="w-full max-w-md rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-primary)] p-6 shadow-xl"
			>
				<h2
					id="confirmation-dialog-title"
					className="text-lg font-semibold text-[var(--color-text-primary)]"
				>
					{title}
				</h2>
				<p
					id="confirmation-dialog-description"
					className="mt-2 text-sm text-[var(--color-text-secondary)]"
				>
					{description}
				</p>
				<div className="mt-6 flex justify-end gap-3">
					<button
						type="button"
						onClick={onCancel}
						className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm font-medium hover:bg-[var(--color-bg-secondary)]"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={onConfirm}
						className="rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
					>
						{confirmLabel}
					</button>
				</div>
			</div>
		</div>
	);
};
